class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.dropdownsOpen = false;

		this.addEventListener('optionchange', e => this.updateSelected(e.detail.trusted));

		let preorderPopup = this.querySelector('.variant-selector__preorder-popup');

		preorderPopup.querySelector('.preorder-popup__back').addEventListener('click', () => this.closePreorderPopup());

		preorderPopup.querySelector('.preorder-popup__atc').addEventListener('click', async e => {
			let btn = e.currentTarget;
			btn.classList.add('show-loading');
			if (this.preorderAtc) await this.preorderAtc();
			btn.classList.remove('show-loading');
			this.closePreorderPopup();
		});

		preorderPopup.addEventListener('click', e => {
			// Close if clicking on popup (ie, the background) instead of its contents 
			if (e.target === e.currentTarget) {
				this.closePreorderPopup();
			}
		});

		this.addEventListener('optionclick', e => {
			if (!this.dropdownsOpen && this.dataset.preorderPopup == 'true' && e.detail.trusted && e.detail.preorder && window.innerWidth < 992) this.showPreorderPopup();
		});

		window.addEventListener('growthbookready', () => this.firePriceChangedEvent());
	}

	async connectedCallback() {
		this.firePriceChangedEvent();

		if (this.hasAttribute('lazyload')) {
			const url = new URL(window.location.href);
			url.searchParams.set('view', 'variant-selector-data');

			const resp = await fetch(url);
			const text = await resp.text();
			const doc = new DOMParser().parseFromString(text, 'text/html');

			const newData = doc.querySelector('select[name="id"]');
			const existingData = this.querySelector('select[name="id"]');
			existingData.replaceWith(newData);

			this.removeAttribute('lazyload');
		}

		this.updateSelected();
	}

	firePriceChangedEvent() {
		if (!this.parentElement || !this.parentElement.classList.contains('product-form')) return;
		const select = this.querySelector('select');
		const variant = select.selectedOptions[0].dataset;
		const priceRaw = variant.priceTestPrices && window.getActivePriceTest() ? JSON.parse(variant.priceTestPrices)[window.getActivePriceTestIndex()] : variant.price;
		const price = parseFloat(priceRaw) / 100;
		let event = new CustomEvent('pricechanged', { detail: { price: price } });
		window.dispatchEvent(event);
	}

	buildVariantObj(option) {
	return {
		...option.dataset,
		isBundle: option.dataset.isBundle == 'true',
		addToCartEnabled: option.dataset.addToCartEnabled == 'true',
		price: parseInt(option.dataset.price)
	};
}

/* Announce the currently-selected variant as a change, without waiting for an
   option to be clicked. Used after a colour swap that lands on the carried
   selection, where no radio click ever fires. */
emitChange(trusted = false) {
	const select = this.querySelector('select');
	const option = select.selectedOptions[0];
	if (!option) return;

	const variantObj = this.buildVariantObj(option);

	select.dispatchEvent(new Event('change'));

	this.dispatchEvent(new CustomEvent('variantchange', {
		detail: {
			variant: variantObj,
			trusted: trusted
		}, bubbles: true
	}));

	document.dispatchEvent(new CustomEvent('variant:change', {
		detail: {
			variant: variantObj,
			trusted: trusted
		}, bubbles: true
	}));

	this.firePriceChangedEvent();
}

updateSelected(trusted = false) {
		const select = this.querySelector('select');
		const optionSelectors = this.querySelectorAll('option-selector');
		const attrs = [];

		for (const selector of optionSelectors) {
			if (!selector.dataset.selected) return;
			attrs.push(`[data-option-${selector.dataset.option}="${selector.dataset.selected}"]`);
		}

		const variant = select.querySelector(`option${attrs.join('')}`);

		if (variant) {
			variant.selected = true;
			
			this.querySelectorAll('.variant-selector__price').forEach(elem => elem.innerHTML = variant.dataset.priceTextShort);
			this.dataset.preorder = variant.dataset.status == 'preorder';

			this.querySelector('.preorder-popup__title').innerHTML = variant.dataset.title;
			this.querySelector('.preorder-popup__month').innerHTML = variant.dataset.preorderMonth;
			let bisForm = this.querySelector('bis-form');
			if (bisForm) bisForm.setAttribute('variant', variant.dataset.id);

			const inclusions = this.querySelector('.variant-selector__inclusions');
			if (inclusions) inclusions.innerHTML = variant.dataset.inclusions;

			const mixedReason = this.querySelector('.variant-selector__mixed-reason');
			if (mixedReason) mixedReason.innerHTML = variant.dataset.mixedReason;

			const variantObj = this.buildVariantObj(variant);

			if (this.dataset.selected && this.dataset.selected != variant.value) {
				select.dispatchEvent(new Event('change'));

				this.dispatchEvent(new CustomEvent('variantchange', { 
					detail: { 
						variant: variantObj,
						trusted: trusted
					}, bubbles: true 
				}));

				document.dispatchEvent(new CustomEvent('variant:change', { 
					detail: { 
						variant: variantObj,
						trusted: trusted
					}, bubbles: true 
				}));

				this.firePriceChangedEvent();
			}

			this.selectedVariant = variantObj;
			this.dataset.selected = variant.value;
		}
	}

	async addToCart(quantity = 1, properties = {}) {
		const error = await addToCartGlobal(
			this.selectedVariant.productHandle,
			this.selectedVariant.id,
			quantity,
			this.selectedVariant.isBundle,
			this.selectedVariant.status == 'preorder' ? this.selectedVariant.preorderMonth : null,
			properties
		);

		if (!error) {
			await window.updateCartDrawer();
			window.showCartDrawer(true);

			const nostoElem = this.closest('.nosto_element');
			if (nostoElem && typeof nostojs == 'function') {
				// Nosto attributes conversions to the recommendation's attribution key, not the
				// placement element id — fall back to the element id for campaigns that don't set it
				const attributionElem = this.closest('[attribution-key]');
				const attributionKey = attributionElem && attributionElem.getAttribute('attribution-key') || nostoElem.id;

				nostojs(api => {
					api.recommendedProductAddedToCart(this.selectedVariant.productId, attributionKey);
				});
			}
		}
	}

	async triggerDropdowns(force = false) {
		this.dropdownsOpen = true;
		for (let selector of this.querySelectorAll('option-selector')) {
			if (!selector.dataset.selected || force) {
				let selected = await selector.openDropdown();

				if (!selected) {
					this.dropdownsOpen = false;
					return false;
				} 
			}
		}

		this.dropdownsOpen = false;

		if (this.dataset.preorderPopup == 'true' && this.dataset.preorder == 'true') {
			await this.showPreorderPopup();
			return false;
		}
		else return true;
	}

	async showPreorderPopup() {
		let popup = this.querySelector('.variant-selector__preorder-popup');
		popup.hidden = false;
		popup.classList.add('transition-in');
		document.documentElement.classList.add('modal-open');
		setTimeout(() => popup.classList.remove('transition-in'), 300);

		return new Promise(resolve => {
			this.addEventListener('preorderpopupclose', resolve, { once: true });
		});
	}

	closePreorderPopup() {
		let popup = this.querySelector('.variant-selector__preorder-popup');
		popup.hidden = true;
		popup.classList.add('transition-out');
		document.documentElement.classList.remove('modal-open');
		setTimeout(() => popup.classList.remove('transition-out'), 300);
		this.dispatchEvent(new CustomEvent('preorderpopupclose'));
	}
}

class OptionSelector extends HTMLElement {
	constructor() {
		super();

		this.modal = this.querySelector(':scope > slide-out-modal');
		this.selectionMade = false;
		this.expectedClose = false;

		// The slide-out-modal can close itself (backdrop click, drag-to-swipe, close
		// button). If it closes without an option having been picked, that's a cancel.
		this.modal.addEventListener('modalclose', () => {
			this.dataset.dropdownOpen = 'false';
			if (!this.selectionMade && !this.expectedClose) this.dispatchEvent(new CustomEvent('cancel'));
		});

		// Any option picked (or re-picked) while the dropdown is open means a following
		// modalclose is a selection, not a cancel.
		this.addEventListener('optionclick', () => this.selectionMade = true);
		this.addEventListener('optionchange', () => this.selectionMade = true);

		// Close this dropdown whenever another modal opens (another option-selector's,
		// the sticky bar's, a recommended card's, ...) — the old click-outside behavior,
		// generalized. The size guide is exempt: its modal opens on top of the open
		// dropdown and the user returns to the dropdown via the guide's back button.
		this.onOtherModalOpen = e => {
			if (e.target == this.modal || this.suppressCloseOnOtherModalOpen) return;
			if (this.dataset.dropdownOpen != 'true') return;
			this.closeDropdown();
			this.dispatchEvent(new CustomEvent('cancel'));
		};

		let fieldset = this.querySelector('fieldset');

		fieldset.addEventListener('change', e => {
			this.updateSelected();
			this.dispatchEvent(new CustomEvent('optionchange', { bubbles: true, detail: { trusted: e.isTrusted }}));
			if (this.dataset.preorder != 'true' || this.dataset.closeOnPreorder != 'false') this.closeDropdown();
		});
		
		for (let radio of fieldset.querySelectorAll('.option-selector__button input[type="radio"]')) {
			radio.addEventListener('click', e => {
				if (radio.checked) {
					this.dispatchEvent(new CustomEvent('optionclick', { bubbles: true, detail: { trusted: e.isTrusted, preorder: radio.dataset.preorder == 'true' }}));
					if (this.dataset.preorder != 'true' || this.dataset.closeOnPreorder != 'false') this.closeDropdown();
				}

				if (window.trackFSEvent) {
					window.trackFSEvent('PDP Option Click', {
						value: radio.value,
						name: radio.name,
						preorder: radio.dataset.preorder == 'true',
						lowstock: radio.dataset.lowstock == 'true',
						available: radio.dataset.available == 'true',
						soldout: radio.dataset.soldout == 'true',
					});
				}
			});
		};

		this.dropdownToggle = this.querySelector('button[name="select-option"]');
		this.dropdownToggle.addEventListener('click', () => this.toggleDropdown());
	}

	connectedCallback() {
		document.addEventListener('modalopen', this.onOtherModalOpen);

		// The opensizeguide event bubbles through this element before the size-guide
		// modal opens (synchronously, during the same dispatch), so a flag set here is
		// visible to onOtherModalOpen when the guide's modalopen fires.
		this.addEventListener('opensizeguide', () => {
			this.suppressCloseOnOtherModalOpen = true;
			setTimeout(() => this.suppressCloseOnOtherModalOpen = false);
		});
	}

	disconnectedCallback() {
		document.removeEventListener('modalopen', this.onOtherModalOpen);
	}

	toggleDropdown() {
		if (this.dataset.dropdownOpen == 'true') this.closeDropdown();
		else this.openDropdown();
	}

	closeDropdown() {
		if (this.dataset.dropdownOpen == 'false') return;
		this.dataset.dropdownOpen = 'false';
		this.expectedClose = true;
		this.modal.close();
		this.dispatchEvent(new CustomEvent('dropdownclose', { bubbles: true }));
	}

	async openDropdown() {
		if (this.dataset.dropdownOpen == 'true') return;
		this.selectionMade = false;
		this.expectedClose = false;
		this.dataset.dropdownOpen = 'true';
		this.modal.open();
		this.dispatchEvent(new CustomEvent('dropdownopen', { bubbles: true }));

		return await new Promise(resolve => {
			this.addEventListener('optionclick', () => setTimeout(() => resolve(true), 100), { once: true });
			this.addEventListener('optionchange', () => resolve(true), { once: true });
			this.addEventListener('cancel', () => resolve(false), { once: true });
		});
	}

	updateSelected() {
		let checked = this.querySelector('.option-selector__buttons input:checked');
		if (!checked) return;

		this.dataset.selected = checked.value;
		this.dataset.mixed = checked.value.includes('Special');
		this.dataset.preorder = checked.dataset.preorder == 'true' ? 'true' : 'false';
		this.dataset.lowstock = checked.dataset.lowstock == 'true' ? 'true' : 'false';
		this.dataset.available = checked.dataset.available == 'true' ? 'true' : 'false';
		this.dataset.soldout = checked.dataset.soldout == 'true' ? 'true' : 'false';

		const label = checked.value.split('(')[0].trim();

		// Change the main text of the variant selector
		this.dropdownToggle.querySelector('.select-option__main-text').textContent =
			label.includes('Special') ? `${label}*` : label;

		// Change the stock availability text. The wording is rendered onto the radio by
		// variant-selector.liquid so it lives in one place; a value with no status carries
		// no attribute, and we leave whatever text is already there alone.
		const statusLabel = checked.dataset.statusLabel;
		if (statusLabel !== undefined) {
			this.dropdownToggle.querySelector('.select-option__availability-text').textContent = statusLabel;
		}
	}
}

customElements.define('variant-selector', VariantSelector);
customElements.define('option-selector', OptionSelector);

/* Cache of fetched <variant-selector> markup per swatch product handle, shared
   between instances — a colour swap replaces this element with a fresh one,
   whose dropdown starts cold. Promises are cached so a fetch in flight when
   the dropdown reopens is awaited, not restarted. */
const colourTemplateCache = new Map();

/* One in-flight document per swatch collection, holding every one of its
   products' selectors (collection.variant-selectors view). Shared across
   instances too, so several dropdowns onto the same collection — the Buy More
   & Save rows all sit on linen bedding — fetch it once between them. */
const colourCollectionCache = new Map();

const fetchColourSelector = handle =>
	fetch(`/products/${handle}?view=variant-selector-full`)
		.then(resp => resp.text())
		.then(text => new DOMParser().parseFromString(text, 'text/html').querySelector('variant-selector'));

/* Colour dropdown (variant-selector colour: true). Shows the swatch collection
   as a grid; picking one fetches that product's variant-selector markup (the
   variant-selector-full view) and replaces the whole element with it, keeping
   the current size. Fetches are prefetched when the dropdown opens. */
class ColourSwatchSelector extends HTMLElement {
	constructor() {
		super();

		this.trigger = this.querySelector('.colour-swatch-selector__trigger');
		this.modal = this.querySelector('slide-out-modal');
		this.grid = this.querySelector('.colour-swatch-selector__grid');
		this.dropdown = this.querySelector('.colour-swatch-selector__dropdown');

		this.trigger.addEventListener('click', () => {
			this.prefetch();
			this.modal.toggle();
		});

		this.modal.addEventListener('modalopen', () => {
			this.dataset.dropdownOpen = 'true';
			this.trigger.setAttribute('aria-expanded', 'true');
		});

		// modalstartclose, not modalclose, so the arrow tracks the closing
		// animation's start rather than its end — same as the sticky ATC trigger.
		this.modal.addEventListener('modalstartclose', () => {
			this.dataset.dropdownOpen = 'false';
			this.trigger.setAttribute('aria-expanded', 'false');
		});

		this.grid.addEventListener('click', e => {
			const swatch = e.target.closest('[data-handle]');
			if (swatch) this.select(swatch);
		});

		// Keep tooltips inside the panel: flip below the swatch when the panel's
		// top edge would clip one, and pull edge swatches' tooltips back in.
		// The header's name line follows the pointer across the grid too.
		this.grid.addEventListener('mouseover', e => {
			const item = e.target.closest('.colour-swatch-selector__swatch-item');
			if (!item) return;

			this.correctTooltip(item);
			this.preview(item.querySelector('[data-colour]')?.dataset.colour);
		});

		// Back to naming the colour actually picked once the pointer leaves.
		this.grid.addEventListener('mouseleave', () => this.preview());
	}

	/* Name the colour under the pointer in the panel's header, or — with nothing
	   hovered — the one that's selected, so the line always says which colour
	   the shopper is looking at (WEB-3196). Touch never hovers, so there it only
	   ever reads the selection. */
	preview(colour) {
		const name = this.querySelector('.colour-swatch-selector__selected-name');
		if (!name) return;

		const selected = this.grid.querySelector('.colour-swatch-selector__swatch--selected');
		name.textContent = colour || selected?.dataset.colour || name.textContent;
	}

	/* Mirrors SwatchLink.correctTooltipOverflow on the PDP, against the panel
	   instead of the viewport. */
	correctTooltip(item) {
		const tooltip = item.querySelector('.colour-swatch-selector__tooltip');
		if (!tooltip) return;

		// Reset first so measurements reflect the resting position.
		tooltip.classList.remove('colour-swatch-selector__tooltip--below');
		tooltip.style.transform = '';
		tooltip.style.removeProperty('--arrow-offset');

		requestAnimationFrame(() => {
			const box = this.dropdown.getBoundingClientRect();
			const rect = tooltip.getBoundingClientRect();

			// First-row swatches: the panel clips anything above the grid.
			if (rect.top < box.top + 4) {
				tooltip.classList.add('colour-swatch-selector__tooltip--below');
			}

			const overflowRight = rect.right - (box.right - 8);
			const overflowLeft = (box.left + 8) - rect.left;
			if (overflowRight > 0) {
				tooltip.style.transform = `translate(calc(-50% - ${overflowRight}px))`;
				tooltip.style.setProperty('--arrow-offset', `${overflowRight}px`);
			} else if (overflowLeft > 0) {
				tooltip.style.transform = `translate(calc(-50% + ${overflowLeft}px))`;
				tooltip.style.setProperty('--arrow-offset', `-${overflowLeft}px`);
			}
		});
	}

	get handles() {
		return [...this.grid.querySelectorAll('[data-handle]')].map(el => el.dataset.handle);
	}

	/* Fetch every swatch product's selector markup once, when the dropdown is
	   opened, so picking a swatch is instant after the first open. One request
	   for the whole swatch collection where we know which collection it is,
	   falling back to a per-product fetch for anything the payload misses. */
	prefetch() {
		const missing = this.handles.filter(handle => !colourTemplateCache.has(handle));
		if (!missing.length) return;

		const collection = this.dataset.collection;

		if (!collection) {
			for (const handle of missing) colourTemplateCache.set(handle, fetchColourSelector(handle));
			return;
		}

		if (!colourCollectionCache.has(collection)) {
			colourCollectionCache.set(collection,
				fetch(`/collections/${collection}?view=variant-selectors`)
					.then(resp => resp.ok ? resp.text() : '')
					.then(text => new DOMParser().parseFromString(text, 'text/html'))
					.catch(() => null)
			);
		}

		const payload = colourCollectionCache.get(collection);

		for (const handle of missing) {
			colourTemplateCache.set(handle, payload
				.then(doc => doc && doc.querySelector(`[data-handle="${handle}"] variant-selector`))
				.then(selector => selector || fetchColourSelector(handle))
			);
		}
	}

	/* Mark the picked swatch and repoint the trigger at it, ready for this
	   dropdown to be moved into the selector coming in to replace ours. */
	retarget(swatch) {
		for (const button of this.grid.querySelectorAll('[data-handle]')) {
			const selected = button === swatch;
			button.classList.toggle('colour-swatch-selector__swatch--selected', selected);
			button.setAttribute('aria-pressed', selected ? 'true' : 'false');
		}

		this.querySelector('.colour-swatch-selector__current-swatch').src = swatch.querySelector('img').src;
		this.querySelector('.colour-swatch-selector__current-name').textContent = swatch.dataset.colour;

		// The panel travels with us into the incoming selector, so its header has
		// to name the new colour as well as the trigger does.
		this.preview();

		return this;
	}

	/* Re-apply a selector's uid to markup fetched for another colour. The
	   variant-selector-full and variant-selectors views render whatever product
	   they're asked for with no uid, so ids and radio names come back bare —
	   fine for the PDP's own selector, which has no uid either, but an embedded
	   one (a Buy More & Save row) would land its radios in the product form's
	   own "size" group and unpick the PDP's selection. */
	static reuid(selector, uid) {
		if (!uid) return selector;

		for (const el of selector.querySelectorAll('[id], [for]')) {
			for (const attr of ['id', 'for']) {
				if (el.hasAttribute(attr)) el.setAttribute(attr, `${el.getAttribute(attr)}-${uid}`);
			}
		}

		// Radio names only. select-option and size-guide are addressed by name in
		// CSS (they carry the dropdown's grid placement) and by OptionSelector.
		for (const radio of selector.querySelectorAll('input[type="radio"][name]')) {
			radio.name = `${radio.name}-${uid}`;
		}

		selector.dataset.uid = uid;
		return selector;
	}

	async select(swatch) {
		const handle = swatch.dataset.handle;

		// Re-picking the current colour just closes the dropdown.
		if (swatch.classList.contains('colour-swatch-selector__swatch--selected')) {
			this.modal.close();
			return;
		}

		this.modal.close();

		const oldSelector = this.closest('variant-selector');
		const template = await colourTemplateCache.get(handle);

		// A competing swap (or removal) may already have taken our selector away.
		if (!template || !oldSelector.isConnected) return;

		// Carry the current selection (size, etc.) across to the new product.
		const selections = [...oldSelector.querySelectorAll('option-selector')]
			.map(selector => ({ option: selector.dataset.option, value: selector.dataset.selected }))
			.filter(selection => selection.value);

		const newSelector = ColourSwatchSelector.reuid(document.importNode(template, true), oldSelector.dataset.uid);

		// The collection payload renders no dropdown (every product's would be
		// the same grid), so ours moves across — repointed at the new colour.
		if (!newSelector.querySelector('colour-swatch-selector')) {
			newSelector.insertBefore(
				this.retarget(swatch),
				newSelector.querySelector('option-selector, .variant-selector__preorder-popup')
			);
		}

		oldSelector.replaceWith(newSelector);

		// updateSelected() runs on the new element when it connects, seeding it
		// with the product's default variant; the clicks below then restore the
		// carried selection. If a click changes anything it announces itself via
		// variantchange — if none fires, the carried selection matched the
		// default, so announce it explicitly.
		let changed = false;
		newSelector.addEventListener('variantchange', () => changed = true, { once: true });

		for (const { option, value } of selections) {
			const target = newSelector.querySelector(`option-selector[data-option="${option}"]`);
			if (!target) continue;

			const radios = [...target.querySelectorAll('.option-selector__button input[type="radio"]')];
			// The same size if it exists on the new product, else the first
			// orderable one, else whatever there is.
			const radio =
				radios.find(r => r.value === value && !r.disabled) ||
				radios.find(r => !r.disabled && r.dataset.soldout != 'true') ||
				radios.find(r => !r.disabled) ||
				radios[0];

			if (!radio || radio.checked) continue;
			radio.click();
		}

		if (!changed) newSelector.emitChange();

		// Anything holding a direct reference to the old element (eg the sticky
		// ATC's pointer at the PDP selector) can re-bind now.
		newSelector.dispatchEvent(new CustomEvent('variantselectorreplaced', {
			bubbles: true,
			detail: { oldSelector, handle, colour: swatch.dataset.colour }
		}));
	}
}

customElements.define('colour-swatch-selector', ColourSwatchSelector);
